import { useMemo, type CSSProperties } from "react";
import styled from "styled-components";

import {
  AnnotationIcon,
  CloseIcon,
  EditIcon,
  ExternalIcon,
  PanelLeftCloseIcon,
} from "../../icons/glyphs";
import { controlFocusRing, inlineElementIconSize } from "../foundations/controlStyles";
import { CompositeListSurface } from "../foundations/compositeList";
import { interactiveSurfaceCss } from "../foundations/interactiveSurface";
import { xScrollbarStyles } from "../foundations/scrollbar";
import { IconButton, TooltipIconButton } from "../IconButton";
import { formatMediaPlaybackTime } from "../MediaTimelineBar";
import { Tooltip } from "../Tooltip";
import { TruncatedText } from "../TruncatedText";
import { ANNOTATION_CAPTURED_FRAME_PREVIEW_MIN_HEIGHT } from "./MediaAnnotationStudioConstraint";
import { CapturedFramePreview } from "./CapturedFramePreview";
import { annotationStudioText } from "./messages";
import { targetColorCss } from "./targetColor";
import type {
  AnnotationFrameDraft,
  AnnotationMediaSource,
  AnnotationSequence,
  AnnotationStream,
  AnnotationStudioLocale,
  AnnotationStudioViewMode,
  AnnotationTarget,
} from "./types";
import type { CapturedFramePreviewSelection } from "./useCapturedFramePreviewSelection";

export interface CapturedFramePreviewPanelProps {
  readonly locale: AnnotationStudioLocale;
  readonly viewMode: AnnotationStudioViewMode;
  readonly frame: AnnotationFrameDraft;
  readonly outsideFilter: boolean;
  readonly media?: AnnotationMediaSource | null;
  readonly sequences: readonly AnnotationSequence[];
  readonly streams: readonly AnnotationStream[];
  readonly targets: readonly AnnotationTarget[];
  readonly targetNames: ReadonlyMap<string, string>;
  readonly onEdit?: () => void;
  readonly onClose: () => void;
  readonly popup?: boolean;
  readonly canDock?: boolean;
  readonly dockRequiresCompact?: boolean;
  readonly onPlacementChange?: () => void;
  /** `frame`과 같은 draft로 만든 `useCapturedFramePreviewSelection` 결과. */
  readonly selection: CapturedFramePreviewSelection;
}

const Card = styled.div`
  box-sizing: border-box;
  display: flex;
  width: 100%;
  min-width: 0;
  min-height: ${ANNOTATION_CAPTURED_FRAME_PREVIEW_MIN_HEIGHT};
  flex: 1 1 auto;
  flex-direction: column;
  gap: 0.35rem;
  padding: 0.4rem;
  overflow: hidden;
  border: 1px solid ${({ theme }) => theme.color.border.divider};
  border-radius: ${({ theme }) => theme.shape.radius.surface};
  background: ${({ theme }) => theme.color.bg.surface};

  /* Match the pinned frame row while leaving the image viewport on its neutral surface. */
  &[data-outside-frame-filter] {
    background: ${({ theme }) => theme.color.bg.attention};
  }
`;

const Header = styled.div`
  display: grid;
  min-width: 0;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  gap: 0.35rem;
`;

const DockPreviewIcon = styled(PanelLeftCloseIcon)`
  transform: scaleX(-1);
`;

const HeaderActions = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
`;

const Meta = styled.span`
  display: grid;
  min-width: 0;
  max-width: 100%;
  flex: 1 1 18rem;
  grid-template-columns: auto auto minmax(0, 1fr);
  align-items: center;
  gap: 0.45rem;
  color: ${({ theme }) => theme.color.text.subtle};
  font-size: ${({ theme }) => theme.typography.size.caption};
  font-variant-numeric: tabular-nums;
`;

const Badge = styled.span`
  box-sizing: border-box;
  display: inline-flex;
  width: 1.35rem;
  height: 1.35rem;
  align-items: center;
  justify-content: center;
  border: 1px solid ${({ theme }) => theme.color.border.divider};
  border-radius: ${({ theme }) => theme.shape.radius.control};
  background: ${({ theme }) => theme.color.bg.surfaceRaised};
  color: ${({ theme }) => theme.color.text.default};

  ${Card}[data-outside-frame-filter] & {
    background: transparent;
  }

  & > svg {
    width: 0.9rem;
    height: 0.9rem;
  }
`;

const Identity = styled.span`
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  white-space: nowrap;
`;

const Context = styled(TruncatedText)``;

const SlotControls = styled.div`
  display: flex;
  min-width: 0;
  flex: 0 0 auto;
  align-items: center;
  gap: 0.25rem;

  & > button {
    flex: 0 0 auto;
    margin-left: auto;
  }
`;

const SlotColors = styled(CompositeListSurface).attrs({ as: "div" })`
  display: flex;
  width: fit-content;
  min-width: 0;
  flex: 0 1 auto;
  align-items: center;
  background: ${({ theme }) => theme.color.bg.surfaceSunken};
  ${xScrollbarStyles}

  & > [data-c6-tooltip-trigger] {
    flex: 0 0 auto;
  }

  & > [data-c6-tooltip-trigger] + [data-c6-tooltip-trigger] {
    border-left: 1px solid ${({ theme }) => theme.color.border.divider};
  }
`;

const SlotSwatch = styled.i`
  display: block;
  box-sizing: border-box;
  width: ${inlineElementIconSize.xs};
  height: ${inlineElementIconSize.xs};
  flex: 0 0 auto;
  border: 1px solid ${({ theme }) => theme.color.border.default};
  border-radius: ${({ theme }) => theme.shape.radius.control};
  background: var(--c6-annotation-slot-color);

  button:disabled & {
    border-color: ${({ theme }) => theme.color.border.disabled};
    background: ${({ theme }) => theme.color.text.disabled};
  }

  @media (forced-colors: active) {
    background: CanvasText;

    button:disabled & {
      border-color: GrayText;
      background: GrayText;
    }
  }
`;

const SlotToggle = styled(TooltipIconButton)`
  && {
    border-radius: 0;
  }

  &&[aria-pressed="true"] {
    ${({ theme }) =>
      interactiveSurfaceCss({
        rest: {
          background: theme.color.bg.selected,
          border: "transparent",
          foreground: theme.color.text.default,
        },
        hover: {
          background: theme.color.bg.selectedHover,
          border: "transparent",
          foreground: theme.color.text.default,
        },
        active: {
          background: theme.color.bg.selectedActive,
          border: "transparent",
          foreground: theme.color.text.default,
        },
        disabled: {
          background: theme.color.bg.control.disabled,
          border: "transparent",
          foreground: theme.color.text.disabled,
        },
      })}

    @media (forced-colors: active) {
      outline: 1px solid Highlight;
      outline-offset: -1px;
    }

    &:focus-visible {
      ${controlFocusRing("-2px")}
    }
  }

  &&:focus-visible {
    ${controlFocusRing("-2px")}
  }
`;

export function CapturedFramePreviewPanel({
  locale,
  viewMode,
  frame,
  outsideFilter,
  media,
  sequences,
  streams,
  targets,
  targetNames,
  onEdit,
  onClose,
  popup = false,
  canDock = true,
  dockRequiresCompact = false,
  onPlacementChange,
  selection,
}: CapturedFramePreviewPanelProps) {
  const previewLabel = annotationStudioText("framePreview", locale);
  const sequence = sequences.find((candidate) => candidate.id === frame.sequenceId);
  const stream = streams.find((candidate) => candidate.id === frame.streamId);
  const previewMedia = media === undefined ? stream?.media : media;
  const { annotatedTargetIds, selectedTargetIds, visibleTargetIds, emphasizedTargetIds } =
    selection;
  const previewTargets = useMemo(
    () =>
      targets.flatMap((target) =>
        annotatedTargetIds.has(target.id) ? [{ target, draft: frame.targets[target.id] }] : [],
      ),
    [annotatedTargetIds, frame, targets],
  );
  const previewOptions = useMemo(
    () =>
      targets.map((target, index) => {
        const draft = frame.targets[target.id];
        return {
          value: target.id,
          label:
            targetNames.get(target.id)?.trim() ||
            `${annotationStudioText("objectSlot", locale)} ${index + 1}`,
          target,
          occupied: annotatedTargetIds.has(target.id),
          includeCount: draft?.points.filter((point) => point.kind === "include").length ?? 0,
          excludeCount: draft?.points.filter((point) => point.kind === "exclude").length ?? 0,
        };
      }),
    [annotatedTargetIds, frame, locale, targets, targetNames],
  );

  return (
    <Card
      role="group"
      aria-label={previewLabel}
      data-captured-frame-preview-panel=""
      data-outside-frame-filter={outsideFilter ? "" : undefined}
    >
      {viewMode === "browse" ? (
        <Header data-captured-frame-preview-header="">
          <Meta>
            <Tooltip content={previewLabel} placement="bottom" describeTrigger={false}>
              <Badge role="img" aria-label={previewLabel} data-annotation-preview-label="">
                <AnnotationIcon aria-hidden="true" />
              </Badge>
            </Tooltip>
            <Identity>{formatMediaPlaybackTime(frame.timestampSeconds)}</Identity>
            <Context data-frame-context="preview">
              {sequence?.label ?? frame.sequenceId} · {stream?.label ?? frame.streamId}
            </Context>
          </Meta>
          <HeaderActions>
            {onPlacementChange ? (
              <TooltipIconButton
                data-preview-placement-toggle=""
                size="sm"
                variant="ghost"
                tooltip={annotationStudioText(
                  popup
                    ? dockRequiresCompact
                      ? "dockFramePreviewCompact"
                      : "dockFramePreview"
                    : "detachFramePreview",
                  locale,
                )}
                aria-label={annotationStudioText(
                  popup ? "dockFramePreview" : "detachFramePreview",
                  locale,
                )}
                disabled={popup && !canDock}
                disabledTooltip={annotationStudioText("dockFramePreviewUnavailable", locale)}
                onClick={onPlacementChange}
                onKeyDown={(event) => {
                  if (event.key === " ") event.stopPropagation();
                }}
              >
                {popup ? <DockPreviewIcon /> : <ExternalIcon />}
              </TooltipIconButton>
            ) : null}
            {onEdit ? (
              <TooltipIconButton
                data-preview-annotation-action=""
                size="sm"
                variant="ghost"
                aria-label={annotationStudioText("editAnnotation", locale)}
                tooltip={{
                  content: annotationStudioText("editAnnotation", locale),
                  placement: "bottom",
                }}
                onClick={onEdit}
              >
                <EditIcon aria-hidden="true" />
              </TooltipIconButton>
            ) : null}
            <IconButton
              size="sm"
              variant="ghost"
              aria-label={annotationStudioText("closeFramePreview", locale)}
              onClick={onClose}
            >
              <CloseIcon />
            </IconButton>
          </HeaderActions>
        </Header>
      ) : null}
      {targets.length > 0 ? (
        <SlotControls
          role="group"
          aria-label={annotationStudioText("objectSlots", locale)}
          data-captured-frame-preview-slot-controls=""
        >
          <SlotColors data-preview-slot-colors="">
            {previewOptions.map((option) => {
              const visible = selectedTargetIds.has(option.value);
              const summary = `${option.label} · ${annotationStudioText("positiveMarker", locale)} ${option.includeCount} · ${annotationStudioText("negativeMarker", locale)} ${option.excludeCount}`;
              return (
                <SlotToggle
                  key={option.value}
                  data-preview-slot={option.value}
                  size="xs"
                  variant="surface"
                  border={false}
                  tone="neutral"
                  selected={visible}
                  disabled={!option.occupied}
                  aria-label={`${previewLabel}: ${option.label}`}
                  tooltip={{ content: summary, placement: "top" }}
                  disabledTooltip={`${summary} · ${annotationStudioText("emptyAnnotation", locale)}`}
                  onKeyDown={(event) => {
                    // Preserve native button activation before the Studio playback shortcut.
                    if (event.key === " ") event.stopPropagation();
                  }}
                  onClick={() => selection.toggleTarget(option.value)}
                  onMouseEnter={() => selection.setHoveredTarget(option.value)}
                  onMouseLeave={() => selection.setHoveredTarget(null)}
                  onFocus={() => selection.setFocusedTarget(option.value)}
                  onBlur={() => selection.setFocusedTarget(null)}
                >
                  <SlotSwatch
                    aria-hidden="true"
                    data-visible={visible}
                    style={
                      {
                        "--c6-annotation-slot-color": targetColorCss(option.target.color),
                      } as CSSProperties
                    }
                  />
                </SlotToggle>
              );
            })}
          </SlotColors>
          <IconButton
            size="xs"
            variant="ghost"
            border
            tone="neutral"
            text={annotationStudioText("showAllPreviewTargets", locale)}
            disabled={annotatedTargetIds.size === 0}
            onKeyDown={(event) => {
              if (event.key === " ") event.stopPropagation();
            }}
            onClick={selection.showAllTargets}
          />
        </SlotControls>
      ) : null}
      <CapturedFramePreview
        frame={frame}
        targets={previewTargets}
        mediaSrc={previewMedia?.src}
        poster={previewMedia?.poster}
        crossOrigin={previewMedia?.crossOrigin}
        visibleTargetIds={visibleTargetIds}
        emphasizedTargetIds={emphasizedTargetIds}
        label={`${annotationStudioText("framePreview", locale)}: ${
          sequence?.label ?? frame.sequenceId
        } · ${formatMediaPlaybackTime(frame.timestampSeconds)}`}
      />
    </Card>
  );
}
